/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * TierPill — compact color-coded badge showing which engagement tier a
 * score falls into. Drop it next to any numeric avg_score / voter score
 * value so users see "Hot" instead of having to mentally map 62 → red.
 *
 * Three ways to call it:
 *   <TierPill score={62} />                 // auto-tier from score
 *   <TierPill tier="hot" />                  // explicit tier key
 *   <TierPill score={62} size="sm" dot />    // tiny, dot-only
 *
 * Every pill is also an InfoTip into the matching tier glossary entry
 * so hovering explains the tier in one sentence.
 */
import React from 'react';
import { tierFromScore, tierFromBackendKey, TIER_META } from '@/lib/tiers';
import type { TierKey } from '@/lib/tiers';
import { InfoTip } from './InfoTip';

type Props = {
  /** Raw 0 – 100 score. Ignored if `tier` is set. */
  score?: number | null;
  /** Explicit tier key (lowercase). Wins over `score`. */
  tier?: TierKey;
  /** Backend-style enum value (SUPER_VOTER / HOT / WARM / COOL). Wins over score. */
  backendKey?: string;
  /** 'sm' = tight inline chip, 'md' (default) = stat-tile chip. */
  size?: 'sm' | 'md';
  /** Dot-only: render just the color swatch with a title attribute. */
  dot?: boolean;
  /** Suppress the "Learn more" popover (useful inside table cells). */
  noTooltip?: boolean;
  className?: string;
};

export const TierPill: React.FC<Props> = ({
  score,
  tier,
  backendKey,
  size = 'md',
  dot = false,
  noTooltip = false,
  className = '',
}) => {
  const meta = tier
    ? TIER_META[tier]
    : backendKey
    ? tierFromBackendKey(backendKey)
    : tierFromScore(score);

  if (dot) {
    return (
      <span
        className={`inline-block rounded-sm ${
          size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2'
        } ${className}`}
        style={{ backgroundColor: meta.hex }}
        title={`${meta.label} (${meta.range[0]}–${meta.range[1]})`}
      />
    );
  }

  const sizeClass =
    size === 'sm'
      ? 'text-[9px] px-1 py-px'
      : 'text-[10px] px-1.5 py-0.5';

  const pill = (
    <span
      className={`inline-flex items-center gap-1 rounded font-bold uppercase tracking-wide border ${sizeClass} ${meta.bgClass} ${meta.textClass} ${meta.borderClass} ${className}`}
      style={{ borderLeft: `3px solid ${meta.hex}` }}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-sm"
        style={{ backgroundColor: meta.hex }}
      />
      {meta.label}
    </span>
  );

  if (noTooltip) return pill;

  return (
    <InfoTip termId={meta.glossaryId} showIcon={false}>
      {pill}
    </InfoTip>
  );
};

export default TierPill;
